import type { Change, Snapshot, SnapshotProcess } from '@legalhermes/shared/domain';
import { dedupeProcesses, getTimeAgo, sortByLastActuacionDesc } from './cases.helpers';

export interface CaseEntry {
  key: string;
  nombre: string;
  searchType: 'name' | 'process';
  processes: SnapshotProcess[];
  ultimoMovimiento: string | null;
}

export function buildCaseEntries(allData: Record<string, Snapshot>): CaseEntry[] {
  return Object.entries(allData).map(([key, snapshot]) => {
    const searchType: 'name' | 'process' = snapshot.searchType === 'process' ? 'process' : 'name';
    const processes = sortByLastActuacionDesc(dedupeProcesses(snapshot.processes || []));
    // processes ya viene ordenado DESC con nulls al final → el primero es el movimiento más reciente
    const ultimoMovimiento = processes[0]?.fechaUltimaActuacion ?? null;

    return {
      key,
      nombre: snapshot.searchName,
      searchType,
      processes,
      ultimoMovimiento,
    };
  });
}

export interface Novedad {
  caseKey: string;
  nombre: string;
  texto: string;
  hace: string;
  timestamp: number;
  count: number;
}

const NOVEDADES_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** Agrupa cambios recientes por caso, enlazando por processId a través de los snapshots. */
export function buildNovedades(changes: Change[], cases: CaseEntry[]): Map<string, Novedad> {
  // processId -> caseKey y caseKey -> nombre, en una sola pasada por los casos
  const processToCase = new Map<number, string>();
  const caseName = new Map<string, string>();
  for (const c of cases) {
    caseName.set(c.key, c.nombre);
    for (const p of c.processes) {
      processToCase.set(p.idProceso, c.key);
    }
  }

  const cutoff = Date.now() - NOVEDADES_WINDOW_MS;
  const result = new Map<string, Novedad>();
  for (const change of changes) {
    if (change.idProceso == null) continue;
    const caseKey = processToCase.get(change.idProceso);
    if (!caseKey) continue;
    const ts = new Date(change.timestamp).getTime();
    if (Number.isNaN(ts) || ts < cutoff) continue;
    const existing = result.get(caseKey);
    if (existing) {
      existing.count += 1;
      existing.timestamp = Math.max(existing.timestamp, ts);
    } else {
      result.set(caseKey, {
        caseKey,
        nombre: caseName.get(caseKey) ?? '',
        texto: '',
        hace: '',
        timestamp: ts,
        count: 1,
      });
    }
  }

  // Deriva texto/hace una vez con los totales finales
  for (const novedad of result.values()) {
    novedad.texto =
      novedad.count === 1 ? '1 nueva actuación' : `${novedad.count} nuevas actuaciones`;
    novedad.hace = getTimeAgo(new Date(novedad.timestamp));
  }
  return result;
}
